import {
  Badge,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  Typography,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bodyWidth, sharedColors } from '../../utils/Style';
import { settingsAtom } from '../../atoms/Settings';
import { SearchBox } from '../SearchBox/SearchBox';
import { useThemeColor } from '../../hooks/useThemeColor';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { useClickMenuItem } from './useClickMenuItem';
import { useBasket } from '../../hooks/useBasket';
import { isTokenValid } from '../../utils/Auth';
import { OffersButton } from './OffersButton';
import { getSelectedLocale, setSelectedLocale } from '../../utils/Localization';
import { allowedLanguages } from '../../constants/Translation';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type MenuItemButtonProps = {
  menuItem: NavMenuItem;
};

const MenuItemButton = ({ menuItem }: MenuItemButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { primary, secondary, ternary } = useThemeColor();

  const settings = useRecoilValue(settingsAtom);

  const onClick = useClickMenuItem();

  const vertical = menuItem.items.reduce(
    (acc, item) => acc && !item.items.length,
    true,
  );

  return (
    <Box
      onMouseEnter={(ev) => setAnchorEl(ev.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Button
        onClick={() => onClick(menuItem)}
        sx={{
          color: secondary,
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          lineHeight: '20px',
          p: '15px 18px',
          height: 50,
          transition: 'color 0.5s ease',
          textTransform: 'none',
          '&:hover': {
            color: ternary,
            backgroundColor: 'transparent',
          },
          ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
            ?.topLevelItemStyle,
        }}
      >
        {menuItem.name}
      </Button>

      <Popper open={!!anchorEl && !!menuItem.items.length} anchorEl={anchorEl}>
        <Paper
          sx={{
            display: 'flex',
            flexDirection: vertical ? 'column' : 'row',
            gap: vertical ? 0 : 5,
            p: 1.5,
            ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
              ?.popperStyle,
          }}
        >
          {menuItem.items.map((child, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                alignItems: 'start',
              }}
            >
              <Button
                onClick={() => onClick(child)}
                sx={{
                  color: sharedColors.gray7,
                  mb: child.items.length ? 0.25 : 0,
                  p: 0,
                  minWidth: 0,
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: primary,
                    backgroundColor: 'transparent',
                  },
                  ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
                    ?.secondLevelItemStyle,
                }}
              >
                {child.name}
              </Button>

              {child.items.map((item, j) => (
                <Button
                  key={j}
                  onClick={() => onClick(item)}
                  sx={{
                    color: sharedColors.gray6,
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: primary,
                      backgroundColor: 'transparent',
                    },
                    ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
                      ?.thirdLevelItemStyle,
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          ))}
        </Paper>
      </Popper>
    </Box>
  );
};

export const AppHeaderDesktop = () => {
  const t = useLocalizedText();

  const settings = useRecoilValue(settingsAtom);

  const { primary, secondary } = useThemeColor();

  const { totalQuantity } = useBasket();

  const navigateTo = useCallback(
    (url: string) => window.open(url, '_blank'),
    [],
  );
  const navigate = useNavigate();

  const logo = settings.settingsV1.storefront_header_logo;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          py: 1.5,
          ...settings.settingsV2?.appHeaderSettings?.rootStyle,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: bodyWidth,
            gap: 1.5,
            alignItems: 'center',
            ...settings.settingsV2?.appHeaderSettings?.innerStyle,
          }}
        >
          {logo ? (
            <Box
              onClick={() => navigate('')}
              component='img'
              src={logo}
              sx={{
                cursor: 'pointer',
                height: 80,
                width: 'auto',
                ...settings.settingsV2?.appHeaderSettings?.logoStyle,
              }}
            />
          ) : (
            <Typography>
              {settings.settingsV1.storefront_navbar_text}
            </Typography>
          )}

          <Box sx={{ ml: 1 }}>
            {!!settings.settingsV1.storefront_facebook_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_facebook_link)
                }
              >
                <FacebookIcon
                  sx={{
                    color: primary,
                    ...settings.settingsV2?.appHeaderSettings?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_twitter_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_twitter_link)
                }
              >
                <XIcon
                  sx={{
                    color: primary,
                    ...settings.settingsV2?.appHeaderSettings?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_instagram_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_instagram_link)
                }
              >
                <InstagramIcon
                  sx={{
                    color: primary,
                    ...settings.settingsV2?.appHeaderSettings?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_youtube_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_youtube_link)
                }
              >
                <YouTubeIcon
                  sx={{
                    color: primary,
                    ...settings.settingsV2?.appHeaderSettings?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_linkedin_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_linkedin_link)
                }
              >
                <LinkedInIcon
                  sx={{
                    color: primary,
                    ...settings.settingsV2?.appHeaderSettings?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}
          </Box>

          {settings.availableLocales.length > 1 && (
            <Select
              size='small'
              value={getSelectedLocale()}
              onChange={(ev) => {
                setSelectedLocale(ev.target.value);
                window.location.reload();
              }}
            >
              {settings.availableLocales.map((locale) => (
                <MenuItem key={locale} value={locale}>
                  {allowedLanguages.find((lang) => lang.locale === locale)
                    ?.name ?? locale}
                </MenuItem>
              ))}
            </Select>
          )}

          <Box sx={{ ml: 'auto' }} />

          <SearchBox placeholder={t('header.type_to_search')} width={290} />

          <Box>
            <IconButton onClick={() => navigate('/favoriler')}>
              <FavoriteBorderIcon
                sx={{
                  ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                }}
              />
            </IconButton>

            <IconButton onClick={() => navigate('/sepet')}>
              <Badge
                badgeContent={totalQuantity.toString()}
                sx={{
                  '& .MuiBadge-badge': {
                    color: secondary,
                    backgroundColor: primary,
                    ...settings.settingsV2?.appHeaderSettings?.iconBadgeStyle,
                  },
                }}
                overlap='circular'
                style={{ transform: 'translate(25px, -15px)' }}
              />
              <ShoppingBasketIcon
                sx={{
                  ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                }}
              />
            </IconButton>

            <OffersButton />

            <IconButton
              onClick={() =>
                navigate(isTokenValid() ? '/account' : '/giris-yap')
              }
            >
              <PersonOutlineIcon
                sx={{
                  ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: primary,
          width: '100%',
          justifyContent: 'center',
          ...settings.settingsV2?.appHeaderSettings?.navMenuSettings?.rootStyle,
        }}
      >
        <Box
          sx={{
            width: bodyWidth,
            display: 'flex',
            flexDirection: 'row',
            ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
              ?.innerStyle,
          }}
        >
          {settings.menu.map((item, i) => (
            <MenuItemButton key={i} menuItem={item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
